import {useEffect, useMemo} from 'react';

import {cachedThunk} from 'shared/cache';
import {EThunkCacheTag} from 'shared/cache/thunk-cache';
import {isEnumValue, getPrefixedSection} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';
import {selectIsLoadingUserFunctions, selectUserFunctions} from 'store/slices/user-slice/user-slice-selectors';
import {loadUserFunctions} from 'store/slices/user-slice/user-slice-thunks';

export interface IUseUserFunctionsArgs {
    sections: string[];
}

const loading: {[index: string]: boolean} = {};

export const useUserRoleFunctions = ({
    sections,
}: IUseUserFunctionsArgs) => {
    const prefixedSections: string[] = sections.reduce((acc: string[], section) => {
        acc.push(getPrefixedSection(section));
        return acc;
    }, []);

    const dispatch = useAppDispatch();

    const userFunctionsLoadingStatuses = useAppSelector(s => selectIsLoadingUserFunctions(s, prefixedSections));
    const userFunctions = useAppSelector(s => selectUserFunctions(s, prefixedSections));

    const userFunctionsMerged = useMemo(() => {
        if (!userFunctions) return undefined;
        let functions: EUserRoleFunction[] = [];
        Object.entries(userFunctions).forEach(([, funcs]) => {
            functions = [...functions, ...(funcs ?? [])];
        });
        return functions;
    }, [userFunctions]);

    const isLoadingUserFunctions = Object.entries(userFunctionsLoadingStatuses).some(([, status]) => status);

    const createUserFunctionsValidator = (section: string) => {
        const hasUserRoleFunction = (userFunction: EUserRoleFunction) => userFunctions[section]?.includes(userFunction);

        return {
            hasUserRoleFunction,
        };
    };

    useEffect(() => {
        if (prefixedSections[0] !== '') {
            prefixedSections.forEach(section => {
                if (!loading[section]) {
                    loading[section] = true;
                    dispatch(cachedThunk(loadUserFunctions, {
                        section,
                    }, {expire: 'never', providesTags: [EThunkCacheTag.USER_FUNCTIONS]})).finally(() => {
                        delete loading[section];
                    });
                }
            });
        }
    }, []);

    const hasUserRoleFunction = (func: EUserRoleFunction | string): boolean => {
        if (isEnumValue(EUserRoleFunction, func)) return !!userFunctionsMerged?.includes(func);

        console.warn(`Неизвестная функция ролевой модели: ${func}`);
        return false;
    };

    return {
        userFunctions,
        userFunctionsLoadingStatuses,
        isLoadingUserFunctions,
        createUserFunctionsValidator,
        hasUserRoleFunction,
    };
};
